import React, { useState } from "react";
import { Container, Row, Col, Card, Form, Button, Alert } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/FacotApplicationForm.css";

const FacotApplicationForm = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        applicantName: "",
        qualifications: {
            bot: { field: "", year: "", institution: "" },
            mot: { field: "", year: "", institution: "" },
            phd: { field: "", year: "", institution: "" },
        },
        additionalQualifications: [],
        aiotaMembershipNo: "",
        aiotaYear: "",
        acotRegistrationNo: "",
        acotYear: "",
        mailingAddress: "",
        pin: "",
        telNo: "",
        mobileNo: "",
        email: "",
        officialPosition: {
            institutionNameAddress: "",
            totalWorkExperience: "",
        },
        facultyPosition: {
            institutionNameAddress: "",
            totalWorkExperience: "",
        },
        selfEmployed: {
            institutionNameAddress: "",
            totalWorkExperience: "",
        },
        publicationsCount: "1", // Initial count matches initial array length
        publications: [{ title: "", name: "", volNoIssue: "", year: "", publisher: "", file: null, filePreview: null }],
        otherPublications: "",
        presentationsCount: "1", // Initial count matches initial array length
        presentations: [{ title: "", conferenceName: "", place: "", year: "", organisation: "", file: null, filePreview: null }],
        otherPresentations: "",
        coteCount: "1", // Initial count matches initial array length
        cote: [{ title: "", place: "", year: "", organisation: "", file: null, filePreview: null }],
        otherCote: "",
        awardsPresentationsCount: "1", // Initial count matches initial array length
        awardsPresentations: [{ details: "", file: null, filePreview: null }],
        principalGuideCount: "1", // Initial count matches initial array length
        principalGuide: [{ studentName: "", dissertationTitle: "", submissionYear: "", file: null, filePreview: null }],
        otherPrincipalGuide: "",
        distinguishedAwardsCount: "1", // Initial count matches initial array length
        distinguishedAwards: [{ details: "", file: null, filePreview: null }],
        otherDistinguishedAwards: "",
        supportingDocuments: [], // For file uploads
        undertaking: false,
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Handle count changes for sections
    const handleCountChange = (section, countField) => (e) => {
        const newCount = e.target.value;
        setFormData((prev) => {
            const currentCount = parseInt(prev[countField] || "0");
            const currentItems = prev[section];
            let updatedItems = [...currentItems];

            if (newCount > currentCount) {
                // Add new empty items
                const emptyItem = getEmptyItem(section);
                for (let i = currentCount; i < newCount; i++) {
                    updatedItems.push(emptyItem);
                }
            } else if (newCount < currentCount) {
                // Remove excess items
                updatedItems = updatedItems.slice(0, newCount);
            }

            return {
                ...prev,
                [countField]: newCount,
                [section]: updatedItems,
            };
        });
    };

    // Get empty item based on section
    const getEmptyItem = (section) => {
        switch (section) {
            case "publications":
                return { title: "", name: "", volNoIssue: "", year: "", publisher: "", file: null, filePreview: null };
            case "presentations":
                return { title: "", conferenceName: "", place: "", year: "", organisation: "", file: null, filePreview: null };
            case "cote":
                return { title: "", place: "", year: "", organisation: "", file: null, filePreview: null };
            case "awardsPresentations":
                return { details: "", file: null, filePreview: null };
            case "principalGuide":
                return { studentName: "", dissertationTitle: "", submissionYear: "", file: null, filePreview: null };
            case "distinguishedAwards":
                return { details: "", file: null, filePreview: null };
            default:
                return {};
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleNestedChange = (section, field, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: { ...prev[section], [field]: value },
        }));
    };

    const handleQualificationChange = (qual, field, value) => {
        setFormData((prev) => ({
            ...prev,
            qualifications: {
                ...prev.qualifications,
                [qual]: { ...prev.qualifications[qual], [field]: value },
            },
        }));
    };

    const handleAdditionalQualificationChange = (index, field, value) => {
        setFormData((prev) => {
            const updatedQualifications = [...prev.additionalQualifications];
            updatedQualifications[index] = { ...updatedQualifications[index], [field]: value };
            return { ...prev, additionalQualifications: updatedQualifications };
        });
    };

    const handleArrayChange = (section, index, field, value) => {
        setFormData((prev) => {
            const updatedArray = [...prev[section]];
            updatedArray[index] = { ...updatedArray[index], [field]: value };
            return { ...prev, [section]: updatedArray };
        });
    };

    const handleFileChange = (section, index, e) => {
        const file = e.target.files[0];
        if (file) {
            // 10MB size validation
            if (file.size > 10 * 1024 * 1024) {
                alert("File size exceeds 10MB. Please upload a smaller file.");
                e.target.value = "";
                return;
            }
            const filePreview = URL.createObjectURL(file);
            setFormData((prev) => {
                const updatedArray = [...prev[section]];
                updatedArray[index] = { ...updatedArray[index], file, filePreview };
                return { ...prev, [section]: updatedArray };
            });
        }
    };

    const addField = (section, emptyObj) => {
        setFormData((prev) => {
            const updatedArray = [...prev[section], emptyObj];
            const countField = `${section}Count`;
            return {
                ...prev,
                [section]: updatedArray,
                [countField]: updatedArray.length.toString(),
            };
        });
    };

    const removeField = (section, index) => {
        setFormData((prev) => {
            const updatedArray = [...prev[section]];
            updatedArray.splice(index, 1);
            const countField = `${section}Count`;
            return {
                ...prev,
                [section]: updatedArray,
                [countField]: updatedArray.length.toString(),
            };
        });
    };

    const addAdditionalQualification = () => {
        addField("additionalQualifications", { field: "", year: "", institution: "" });
    };

    const removeAdditionalQualification = (index) => {
        removeField("additionalQualifications", index);
    };

    const handleFileUploadChange = (e) => {
        const files = Array.from(e.target.files);
        // 10MB size validation for each file
        const largeFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (largeFiles.length > 0) {
            alert("One or more files exceed 10MB. Please upload smaller files.");
            e.target.value = "";
            return;
        }
        setFormData((prev) => ({
            ...prev,
            supportingDocuments: [...prev.supportingDocuments, ...files],
        }));
    };

    const handleCheckboxChange = (e) => {
        setFormData((prev) => ({ ...prev, undertaking: e.target.checked }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.applicantName) newErrors.applicantName = "Applicant Name is required";
        if (!formData.aiotaMembershipNo) newErrors.aiotaMembershipNo = "AIOTA Membership No. is required";
        if (!formData.acotRegistrationNo) newErrors.acotRegistrationNo = "ACOT Registration No. is required";
        if (!formData.mailingAddress) newErrors.mailingAddress = "Mailing Address is required";
        if (!formData.email) newErrors.email = "Email is required";
        if (!formData.undertaking) newErrors.undertaking = "You must agree to the undertaking";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        setLoading(true);
        setApiError(null);
        setSuccessMessage(null);

        try {
            // Double-check required fields before sending
            if (!formData.applicantName || !formData.email) {
                setApiError('Required fields (Name and Email) must be filled.');
                setLoading(false);
                return;
            }

            // Create FormData for file uploads
            const formDataToSend = new FormData();

            // Add basic form fields - ensure required fields are not empty
            formDataToSend.append('applicantName', formData.applicantName.trim());
            formDataToSend.append('email', formData.email.trim());
            formDataToSend.append('aiotaMembershipNo', formData.aiotaMembershipNo || '');
            formDataToSend.append('aiotaYear', formData.aiotaYear || '');
            formDataToSend.append('acotRegistrationNo', formData.acotRegistrationNo || '');
            formDataToSend.append('acotYear', formData.acotYear || '');
            formDataToSend.append('mailingAddress', formData.mailingAddress || '');
            formDataToSend.append('pin', formData.pin || '');
            formDataToSend.append('telNo', formData.telNo || '');  // Changed from 'telephone'
            formDataToSend.append('mobileNo', formData.mobileNo || '');  // Changed from 'mobile'
            formDataToSend.append('declarationAccepted', formData.undertaking);

            // Add qualifications - match schema field names
            const qualifications = {
                BOT: formData.qualifications.bot.field || '',
                BOTYearOfPassing: formData.qualifications.bot.year || '',
                BOTInstitution: formData.qualifications.bot.institution || '',
                MOT: formData.qualifications.mot.field || '',
                MOTYearOfPassing: formData.qualifications.mot.year || '',
                MOTInstitution: formData.qualifications.mot.institution || '',
                PHD: formData.qualifications.phd.field || '',
                PHDYearOfPassing: formData.qualifications.phd.year || '',
                PHDInstitution: formData.qualifications.phd.institution || ''
            };
            formDataToSend.append('qualifications', JSON.stringify(qualifications));

            // Add work experience sections - match schema field names
            const officialPosition = {
                institution: formData.officialPosition.institutionNameAddress || '',
                workExperience: formData.officialPosition.totalWorkExperience || ''
            };
            formDataToSend.append('officialPosition', JSON.stringify(officialPosition));

            const facultyPosition = {
                institution: formData.facultyPosition.institutionNameAddress || '',
                workExperience: formData.facultyPosition.totalWorkExperience || ''
            };
            formDataToSend.append('facultyPosition', JSON.stringify(facultyPosition));

            const selfEmployed = {
                institution: formData.selfEmployed.institutionNameAddress || '',
                workExperience: formData.selfEmployed.totalWorkExperience || ''
            };
            formDataToSend.append('selfEmployed', JSON.stringify(selfEmployed));

            // Add array sections - match schema field names
            const publications = formData.publications.map(pub => ({
                title: pub.title || '',
                nameOfPublication: pub.name || '',
                volumeIssue: pub.volNoIssue || '',
                year: pub.year || '',
                publisher: pub.publisher || '',
                fileUrl: '' // Will be updated after file upload
            }));
            formDataToSend.append('publications', JSON.stringify(publications));

            const presentations = formData.presentations.map(pres => ({
                title: pres.title || '',
                conferenceName: pres.conferenceName || '',
                place: pres.place || '',
                year: pres.year || '',
                organization: pres.organisation || '',
                fileUrl: '' // Will be updated after file upload
            }));
            formDataToSend.append('presentations', JSON.stringify(presentations));

            const seminars = formData.cote.map(item => ({
                title: item.title || '',
                place: item.place || '',
                year: item.year || '',
                organization: item.organisation || '',
                fileUrl: '' // Will be updated after file upload
            }));
            formDataToSend.append('cote', JSON.stringify(seminars));  // Changed from 'seminars'

            const awards = formData.awardsPresentations.map(award => ({
                title: award.details || '',
                fileUrl: '' // Will be updated after file upload
            }));
            formDataToSend.append('awards', JSON.stringify(awards));

            const guides = formData.principalGuide.map(guide => ({
                studentName: guide.studentName || '',
                dissertationTitle: guide.dissertationTitle || '',
                yearOfSubmission: guide.submissionYear || '',
                fileUrl: '' // Will be updated after file upload
            }));
            formDataToSend.append('guides', JSON.stringify(guides));

            const distinguishedAwards = formData.distinguishedAwards.map(award => ({
                title: award.details || '',
                fileUrl: '' // Will be updated after file upload
            }));
            formDataToSend.append('distinguishedAwards', JSON.stringify(distinguishedAwards));

            // Add file uploads for each section
            formData.publications.forEach((pub, index) => {
                if (pub.file) {
                    formDataToSend.append(`publication_${index}_file`, pub.file);
                }
            });

            formData.presentations.forEach((pres, index) => {
                if (pres.file) {
                    formDataToSend.append(`presentation_${index}_file`, pres.file);
                }
            });

            formData.cote.forEach((item, index) => {
                if (item.file) {
                    formDataToSend.append(`seminar_${index}_file`, item.file);
                }
            });

            formData.awardsPresentations.forEach((award, index) => {
                if (award.file) {
                    formDataToSend.append(`award_${index}_file`, award.file);
                }
            });

            formData.principalGuide.forEach((guide, index) => {
                if (guide.file) {
                    formDataToSend.append(`guide_${index}_file`, guide.file);
                }
            });

            formData.distinguishedAwards.forEach((award, index) => {
                if (award.file) {
                    formDataToSend.append(`distinguished_award_${index}_file`, award.file);
                }
            });

            // Add supporting documents
            formData.supportingDocuments.forEach((file, index) => {
                formDataToSend.append(`supporting_document_${index}`, file);
            });

            // Debug: Log the data being sent
            console.log('Sending FACOT application data:', {
                applicantName: formData.applicantName,
                email: formData.email,
                aiotaMembershipNo: formData.aiotaMembershipNo,
                acotRegistrationNo: formData.acotRegistrationNo,
                declarationAccepted: formData.undertaking,
                qualifications: qualifications,
                publicationsCount: publications.length,
                presentationsCount: presentations.length,
                seminarsCount: seminars.length
            });

            // Debug: Log FormData contents
            console.log('FormData contents:');
            for (let [key, value] of formDataToSend.entries()) {
                console.log(key, value);
            }

            // Make API call
            const apiUrl = process.env.REACT_APP_BASEURL || 'http://localhost:3000/api';
            console.log('Environment REACT_APP_BASEURL:', process.env.REACT_APP_BASEURL);
            console.log('Making API call to:', `${apiUrl}/facot-applications`);
            
            const response = await axios.post(`${apiUrl}/facot-applications`, formDataToSend, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                setSuccessMessage('Application submitted successfully! Redirecting to payment...');
                // Navigate to payment page after successful submission
                setTimeout(() => {
                    navigate('/facot-payment', {
                        state: {
                            applicationData: response.data.data || {
                                _id: response.data.applicationId,
                                applicantName: formData.applicantName,
                                email: formData.email,
                                aiotaMembershipNo: formData.aiotaMembershipNo,
                                acotRegistrationNo: formData.acotRegistrationNo,
                                createdAt: new Date().toISOString()
                            }
                        }
                    });
                }, 2000);
                // Reset form after successful submission
                setFormData({
                    applicantName: "",
                    qualifications: {
                        bot: { field: "", year: "", institution: "" },
                        mot: { field: "", year: "", institution: "" },
                        phd: { field: "", year: "", institution: "" },
                    },
                    additionalQualifications: [],
                    aiotaMembershipNo: "",
                    aiotaYear: "",
                    acotRegistrationNo: "",
                    acotYear: "",
                    mailingAddress: "",
                    pin: "",
                    telNo: "",
                    mobileNo: "",
                    email: "",
                    officialPosition: {
                        institutionNameAddress: "",
                        totalWorkExperience: "",
                    },
                    facultyPosition: {
                        institutionNameAddress: "",
                        totalWorkExperience: "",
                    },
                    selfEmployed: {
                        institutionNameAddress: "",
                        totalWorkExperience: "",
                    },
                    publicationsCount: "1",
                    publications: [{ title: "", name: "", volNoIssue: "", year: "", publisher: "", file: null, filePreview: null }],
                    otherPublications: "",
                    presentationsCount: "1",
                    presentations: [{ title: "", conferenceName: "", place: "", year: "", organisation: "", file: null, filePreview: null }],
                    otherPresentations: "",
                    coteCount: "1",
                    cote: [{ title: "", place: "", year: "", organisation: "", file: null, filePreview: null }],
                    otherCote: "",
                    awardsPresentationsCount: "1",
                    awardsPresentations: [{ details: "", file: null, filePreview: null }],
                    principalGuideCount: "1",
                    principalGuide: [{ studentName: "", dissertationTitle: "", submissionYear: "", file: null, filePreview: null }],
                    otherPrincipalGuide: "",
                    distinguishedAwardsCount: "1",
                    distinguishedAwards: [{ details: "", file: null, filePreview: null }],
                    otherDistinguishedAwards: "",
                    supportingDocuments: [],
                    undertaking: false,
                });
                setErrors({});
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            console.error('Error response:', error.response?.data);
            
            if (error.response && error.response.data) {
                if (error.response.data.message) {
                    setApiError(error.response.data.message);
                } else if (error.response.data.error) {
                    setApiError(error.response.data.error);
                } else if (error.response.data.errors) {
                    // Handle validation errors from backend
                    const errorMessages = Object.values(error.response.data.errors).join(', ');
                    setApiError(`Validation errors: ${errorMessages}`);
                } else {
                    setApiError(`Server error: ${error.response.status} - ${error.response.statusText}`);
                }
            } else if (error.request) {
                setApiError('Network error: Unable to connect to server. Please check your internet connection.');
            } else {
                setApiError('An unexpected error occurred while submitting your application. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
                  {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>

        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
                <div className="content-container facot-application-content-container pb-5">
                    <section className="facot-application-section">
                        <Container>
                            <h2 className="text-center facot-application-heading mb-4 pt-4">
                                FACOT Application Form
                            </h2>

                            {apiError && <Alert variant="danger">{apiError}</Alert>}
                            {successMessage && <Alert variant="success">{successMessage}</Alert>}

                            <Card className="info-card shadow-sm">
                                <Card.Body>
                                    <Card.Title className="info-card-title">Read Carefully before filling the Application</Card.Title>
                                    <Card.Text className="info-card-text">
                                        <strong>Important:</strong>
                                        <ul>
                                            <li>Application should be complete in all respect.</li>
                                            <li><strong>Self-attested copy in support of all specified information should be uploaded.</strong></li>
                                        </ul>
                                    </Card.Text>
                                </Card.Body>
                            </Card>

                            <Form onSubmit={handleSubmit}>
                                <Container className="my-4">
                                    <h4 className="form-heading-facot-application text-center">Application Form</h4>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">Name of the Applicant</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="applicantName"
                                            className="form-control-facot-application"
                                            value={formData.applicantName}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.applicantName}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.applicantName}</Form.Control.Feedback>
                                    </Form.Group>

                                    <h4 className="form-heading-facot-application">Qualifications</h4>
                                    {["bot", "mot", "phd"].map((qual) => (
                                        <Row key={qual} className="mb-3 qualifications-facot-application">
                                            <Col>
                                                <Form.Label className="form-label-facot-application">{qual.toUpperCase()}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    className="form-control-facot-application"
                                                    value={formData.qualifications[qual].field}
                                                    onChange={(e) => handleQualificationChange(qual, "field", e.target.value)}
                                                />
                                            </Col>
                                            <Col>
                                                <Form.Label className="form-label-facot-application">{qual.toUpperCase()} Year of Passing</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    className="form-control-facot-application"
                                                    value={formData.qualifications[qual].year}
                                                    onChange={(e) => handleQualificationChange(qual, "year", e.target.value)}
                                                />
                                            </Col>
                                            <Col>
                                                <Form.Label className="form-label-facot-application">{qual.toUpperCase()} Institution</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    className="form-control-facot-application"
                                                    value={formData.qualifications[qual].institution}
                                                    onChange={(e) => handleQualificationChange(qual, "institution", e.target.value)}
                                                />
                                            </Col>
                                        </Row>
                                    ))}

                                    {formData.additionalQualifications.map((qual, index) => (
                                        <Row key={`additional-${index}`} className="mb-3 qualifications-facot-application">
                                            <Col>
                                                <Form.Control
                                                    type="text"
                                                    className="form-control-facot-application"
                                                    placeholder="Qualification"
                                                    value={qual.field}
                                                    onChange={(e) => handleAdditionalQualificationChange(index, "field", e.target.value)}
                                                />
                                            </Col>
                                            <Col>
                                                <Form.Control
                                                    type="text"
                                                    className="form-control-facot-application"
                                                    placeholder="Year of Passing"
                                                    value={qual.year}
                                                    onChange={(e) => handleAdditionalQualificationChange(index, "year", e.target.value)}
                                                />
                                            </Col>
                                            <Col>
                                                <Form.Control
                                                    type="text"
                                                    className="form-control-facot-application"
                                                    placeholder="Institution"
                                                    value={qual.institution}
                                                    onChange={(e) => handleAdditionalQualificationChange(index, "institution", e.target.value)}
                                                />
                                            </Col>
                                            <Col xs="auto" className="d-flex align-items-center">
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeAdditionalQualification(index)}
                                                >
                                                    🗑 Remove
                                                </Button>
                                            </Col>
                                        </Row>
                                    ))}

                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="mb-2"
                                        onClick={addAdditionalQualification}
                                    >
                                        + Add Additional Qualification
                                    </Button>

                                    <Row className="mb-3 membership-facot-application">
                                        <Col>
                                            <Form.Label className="form-label-facot-application">AIOTA Membership No.</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="aiotaMembershipNo"
                                                className="form-control-facot-application"
                                                value={formData.aiotaMembershipNo}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.aiotaMembershipNo}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.aiotaMembershipNo}</Form.Control.Feedback>
                                        </Col>
                                        <Col>
                                            <Form.Label className="form-label-facot-application">AIOTA Year</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="form-control-facot-application"
                                                name="aiotaYear"
                                                value={formData.aiotaYear}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="membership-facot-application">
                                        <Col>
                                            <Form.Label className="form-label-facot-application">ACOT Registration No.</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="form-control-facot-application"
                                                name="acotRegistrationNo"
                                                value={formData.acotRegistrationNo}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.acotRegistrationNo}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.acotRegistrationNo}</Form.Control.Feedback>
                                        </Col>
                                        <Col>
                                            <Form.Label className="form-label-facot-application">ACOT Year</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="form-control-facot-application"
                                                name="acotYear"
                                                value={formData.acotYear}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">Mailing Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            className="form-control-facot-application"
                                            rows={3}
                                            name="mailingAddress"
                                            value={formData.mailingAddress}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.mailingAddress}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.mailingAddress}</Form.Control.Feedback>
                                    </Form.Group>

                                    <Row className="mb-3 membership-facot-application">
                                        <Col>
                                            <Form.Label className="form-label-facot-application">Pin</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="form-control-facot-application"
                                                name="pin"
                                                value={formData.pin}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                        <Col>
                                            <Form.Label className="form-label-facot-application">Tel. No.</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="form-control-facot-application"
                                                name="telNo"
                                                value={formData.telNo}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                        <Col>
                                            <Form.Label className="form-label-facot-application">Mobile No.</Form.Label>
                                            <Form.Control
                                                type="text"
                                                className="form-control-facot-application"
                                                name="mobileNo"
                                                value={formData.mobileNo}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">E-mail</Form.Label>
                                        <Form.Control
                                            type="email"
                                            className="form-control-facot-application"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.email}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                    </Form.Group>

                                    <h4 className="form-heading-facot-application">Official Position</h4>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">a. Institution’s Name & Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            className="form-control-facot-application"
                                            rows={2}
                                            value={formData.officialPosition.institutionNameAddress}
                                            onChange={(e) => handleNestedChange("officialPosition", "institutionNameAddress", e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">b. Total Work Experience</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="form-control-facot-application"
                                            value={formData.officialPosition.totalWorkExperience}
                                            onChange={(e) => handleNestedChange("officialPosition", "totalWorkExperience", e.target.value)}
                                        />
                                    </Form.Group>

                                    <h4 className="form-heading-facot-application">Faculty Position in AIOTA Accredited Occupational Therapy Programs</h4>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">a. Institution’s Name & Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            className="form-control-facot-application"
                                            rows={2}
                                            value={formData.facultyPosition.institutionNameAddress}
                                            onChange={(e) => handleNestedChange("facultyPosition", "institutionNameAddress", e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">b. Total Work Experience</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="form-control-facot-application"
                                            value={formData.facultyPosition.totalWorkExperience}
                                            onChange={(e) => handleNestedChange("facultyPosition", "totalWorkExperience", e.target.value)}
                                        />
                                    </Form.Group>

                                    <h4 className="form-heading-facot-application">If Self-Employed</h4>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">a. Institution’s Name & Address</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            className="form-control-facot-application"
                                            rows={2}
                                            value={formData.selfEmployed.institutionNameAddress}
                                            onChange={(e) => handleNestedChange("selfEmployed", "institutionNameAddress", e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="form-label-facot-application">b. Total Work Experience</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="form-control-facot-application"
                                            value={formData.selfEmployed.totalWorkExperience}
                                            onChange={(e) => handleNestedChange("selfEmployed", "totalWorkExperience", e.target.value)}
                                        />
                                    </Form.Group>
                                </Container>

                                <Container className="my-4">
                                    <h4 className="form-heading-facot-application">Publications</h4>
                                    <Form.Group as={Row} className="mb-3 align-items-center">
                                        <Form.Label column sm={9} className="form-label-facot-application">
                                            No. of publications as first author/contributing author/Alternate author
                                            in the indexed journals/books
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                className="form-control-facot-application"
                                                value={formData.publicationsCount}
                                                onChange={handleCountChange("publications", "publicationsCount")}
                                            />
                                        </Col>
                                    </Form.Group>
                                    {parseInt(formData.publicationsCount || "0") > 0 && (
                                        <Form.Group className="mb-3">
                                            {formData.publications.map((pub, index) => (
                                                <Card key={index} className="mb-3 shadow-sm card-facot-application">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="m-0">Publication {index + 1}</h5>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeField("publications", index)}
                                                                disabled={formData.publications.length <= 1}
                                                            >
                                                                🗑 Remove
                                                            </Button>
                                                        </div>
                                                        <Row className="mb-3">
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Title of presentation"
                                                                    value={pub.title}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("publications", index, "title", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Name of Publication"
                                                                    value={pub.name}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("publications", index, "name", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Vol. No/Issue"
                                                                    value={pub.volNoIssue}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("publications", index, "volNoIssue", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Year"
                                                                    value={pub.year}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("publications", index, "year", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Publisher"
                                                                    value={pub.publisher}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("publications", index, "publisher", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    type="file"
                                                                    className="file-input-facot-application mb-2"
                                                                    onChange={(e) => handleFileChange("publications", index, e)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Form.Group>
                                    )}

                                    <h4 className="form-heading-facot-application">Presentations</h4>
                                    <Form.Group as={Row} className="mb-3 align-items-center">
                                        <Form.Label column sm={9} className="form-label-facot-application">
                                            No. of paper presentations in national/international conferences
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                className="form-control-facot-application"
                                                value={formData.presentationsCount}
                                                onChange={handleCountChange("presentations", "presentationsCount")}
                                            />
                                        </Col>
                                    </Form.Group>
                                    {parseInt(formData.presentationsCount || "0") > 0 && (
                                        <Form.Group className="mb-3">
                                            {formData.presentations.map((pres, index) => (
                                                <Card key={index} className="mb-3 shadow-sm card-facot-application">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="m-0">Presentation {index + 1}</h5>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeField("presentations", index)}
                                                                disabled={formData.presentations.length <= 1}
                                                            >
                                                                🗑 Remove
                                                            </Button>
                                                        </div>
                                                        <Row className="mb-3">
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Title of Presentation"
                                                                    value={pres.title}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("presentations", index, "title", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Conference Name"
                                                                    value={pres.conferenceName}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("presentations", index, "conferenceName", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Place"
                                                                    value={pres.place}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("presentations", index, "place", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Year"
                                                                    value={pres.year}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("presentations", index, "year", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Organisation"
                                                                    value={pres.organisation}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("presentations", index, "organisation", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    type="file"
                                                                    className="file-input-facot-application mb-2"
                                                                    onChange={(e) => handleFileChange("presentations", index, e)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Form.Group>
                                    )}

                                    <h4 className="form-heading-facot-application">COTE/CME/Seminar/Workshop/Short Courses</h4>
                                    <Form.Group as={Row} className="mb-3 align-items-center">
                                        <Form.Label column sm={9} className="form-label-facot-application">
                                            No. of COTE/CME/Seminar/Workshop/Short Courses as resource person
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                className="form-control-facot-application"
                                                value={formData.coteCount}
                                                onChange={handleCountChange("cote", "coteCount")}
                                            />
                                        </Col>
                                    </Form.Group>
                                    {parseInt(formData.coteCount || "0") > 0 && (
                                        <Form.Group className="mb-3">
                                            {formData.cote.map((item, index) => (
                                                <Card key={index} className="mb-3 shadow-sm card-facot-application">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="m-0">COTE {index + 1}</h5>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeField("cote", index)}
                                                                disabled={formData.cote.length <= 1}
                                                            >
                                                                🗑 Remove
                                                            </Button>
                                                        </div>
                                                        <Row className="mb-3">
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Title of COTE /CME/Seminar/Workshop etc"
                                                                    value={item.title}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("cote", index, "title", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Place"
                                                                    value={item.place}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("cote", index, "place", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Year"
                                                                    value={item.year}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("cote", index, "year", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Organisation"
                                                                    value={item.organisation}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("cote", index, "organisation", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    type="file"
                                                                    className="file-input-facot-application mb-2"
                                                                    onChange={(e) => handleFileChange("cote", index, e)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Form.Group>
                                    )}

                                    <h4 className="form-heading-facot-application">Awards for Presentations</h4>
                                    <Form.Group as={Row} className="mb-3 align-items-center">
                                        <Form.Label column sm={9} className="form-label-facot-application">
                                            No. of awards for presentations in conferences etc
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                className="form-control-facot-application"
                                                value={formData.awardsPresentationsCount}
                                                onChange={handleCountChange("awardsPresentations", "awardsPresentationsCount")}
                                            />
                                        </Col>
                                    </Form.Group>
                                    {parseInt(formData.awardsPresentationsCount || "0") > 0 && (
                                        <Form.Group className="mb-3">
                                            {formData.awardsPresentations.map((award, index) => (
                                                <Card key={index} className="mb-3 shadow-sm card-facot-application">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="m-0">Award {index + 1}</h5>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeField("awardsPresentations", index)}
                                                                disabled={formData.awardsPresentations.length <= 1}
                                                            >
                                                                🗑 Remove
                                                            </Button>
                                                        </div>
                                                        <Row className="mb-3">
                                                            <Col md={6} sm={12} xs={12}>
                                                                <Form.Control
                                                                    type="text"
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Enter award details"
                                                                    value={award.details}
                                                                    onChange={(e) => {
                                                                        const updated = [...formData.awardsPresentations];
                                                                        updated[index] = { ...updated[index], details: e.target.value };
                                                                        setFormData({ ...formData, awardsPresentations: updated });
                                                                    }}
                                                                />
                                                            </Col>
                                                            <Col md={6} sm={12} xs={12}>
                                                                <Form.Control
                                                                    type="file"
                                                                    className="file-input-facot-application mb-2"
                                                                    onChange={(e) => handleFileChange("awardsPresentations", index, e)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Form.Group>
                                    )}

                                    <h4 className="form-heading-facot-application">Principal Guide</h4>
                                    <Form.Group as={Row} className="mb-3 align-items-center">
                                        <Form.Label column sm={9} className="form-label-facot-application">
                                            No. of students as Principal Guide
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                className="form-control-facot-application"
                                                value={formData.principalGuideCount}
                                                onChange={handleCountChange("principalGuide", "principalGuideCount")}
                                            />
                                        </Col>
                                    </Form.Group>
                                    {parseInt(formData.principalGuideCount || "0") > 0 && (
                                        <Form.Group className="mb-3">
                                            {formData.principalGuide.map((guide, index) => (
                                                <Card key={index} className="mb-3 shadow-sm card-facot-application">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="m-0">Guide {index + 1}</h5>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeField("principalGuide", index)}
                                                                disabled={formData.principalGuide.length <= 1}
                                                            >
                                                                🗑 Remove
                                                            </Button>
                                                        </div>
                                                        <Row className="mb-3">
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Name of Student"
                                                                    value={guide.studentName}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("principalGuide", index, "studentName", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Title of Dissertation"
                                                                    value={guide.dissertationTitle}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("principalGuide", index, "dissertationTitle", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={4} sm={6} xs={12}>
                                                                <Form.Control
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Year of Submission"
                                                                    value={guide.submissionYear}
                                                                    onChange={(e) =>
                                                                        handleArrayChange("principalGuide", index, "submissionYear", e.target.value)
                                                                    }
                                                                />
                                                            </Col>
                                                            <Col md={6} sm={12}>
                                                                <Form.Control
                                                                    type="file"
                                                                    className="file-input-facot-application mb-2"
                                                                    onChange={(e) => handleFileChange("principalGuide", index, e)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Form.Group>
                                    )}

                                    <h4 className="form-heading-facot-application">Distinguished Awards and Honors</h4>
                                    <Form.Group as={Row} className="mb-3 align-items-center">
                                        <Form.Label column sm={9} className="form-label-facot-application">
                                            No. of distinguished awards and honors
                                        </Form.Label>
                                        <Col sm={3}>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                className="form-control-facot-application"
                                                value={formData.distinguishedAwardsCount}
                                                onChange={handleCountChange("distinguishedAwards", "distinguishedAwardsCount")}
                                            />
                                        </Col>
                                    </Form.Group>
                                    {parseInt(formData.distinguishedAwardsCount || "0") > 0 && (
                                        <Form.Group className="mb-3">
                                            {formData.distinguishedAwards.map((award, index) => (
                                                <Card key={index} className="mb-3 shadow-sm card-facot-application">
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h5 className="m-0">Award {index + 1}</h5>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => removeField("distinguishedAwards", index)}
                                                                disabled={formData.distinguishedAwards.length <= 1}
                                                            >
                                                                🗑 Remove
                                                            </Button>
                                                        </div>
                                                        <Row className="mb-3">
                                                            <Col md={6} sm={12}>
                                                                <Form.Control
                                                                    type="text"
                                                                    className="form-control-facot-application mb-2"
                                                                    placeholder="Enter award or honor"
                                                                    value={award.details}
                                                                    onChange={(e) => {
                                                                        const updated = [...formData.distinguishedAwards];
                                                                        updated[index] = { ...updated[index], details: e.target.value };
                                                                        setFormData({ ...formData, distinguishedAwards: updated });
                                                                    }}
                                                                />
                                                            </Col>
                                                            <Col md={6} sm={12}>
                                                                <Form.Control
                                                                    type="file"
                                                                    className="file-input-facot-application mb-2"
                                                                    onChange={(e) => handleFileChange("distinguishedAwards", index, e)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </Form.Group>
                                    )}

                                    <Container className="my-4">
                                        <h4 className="form-heading-facot-application">Upload Self-Attested Copies of Supporting Documents</h4>
                                        <Form.Group className="mb-3">
                                            <Form.Control
                                                type="file"
                                                multiple
                                                className="custom-file-input"
                                                onChange={handleFileUploadChange}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                label="I hereby certify that the information provided by me for Award of Fellowship of Academic Council of Occupational Therapy of AIOTA
                                                        is true to the best of my knowledge and belief. I shall abide by the rules & regulations for submission of my application. I also
                                                        understand that the decision of the ACOT/AIOTA EC in this respect shall be final and binding. "
                                                checked={formData.undertaking}
                                                onChange={handleCheckboxChange}
                                                isInvalid={!!errors.undertaking}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.undertaking}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Container>

                                    <Row className="d-flex justify-content-center">
                                        <Button type="submit" disabled={loading} className="facot-application-button">
                                            {loading ? "Submitting..." : "Submit Application"}
                                        </Button>
                                    </Row>
                                </Container>
                            </Form>
                        </Container>
                    </section>
                </div>
            </div>
        </>
    );
};

export default FacotApplicationForm;